import { Link } from "@tanstack/react-router";
import type React from "react";
import type { ReactNode } from "react";
import { tv } from "tailwind-variants";

const listItemVariants = tv({
  base: [
    "flex h-[34px] w-[231px] items-center",
    "min-h-[48px] w-full py-1 pr-[27px]",
    "text-left font-bold text-[12pt] transition-colors",
    "rounded-[0px_17px_0px_0px]",
  ],
  variants: {
    state: {
      default: "text-variable-collection-dark-gray hover:bg-[#D8E0E3]",
      active: "bg-variable-collection-main text-custom-white",
      selected: "bg-study-group-selected text-white",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

const labelVariants = tv({
  base: ["break-words text-xs leading-[1.2]", "line-clamp-2 max-w-full"],
});

const sublabelVariants = tv({
  base: "truncate text-xs",
  variants: {
    state: {
      default: "text-gray-500",
      active: "text-gray-500",
      selected: "text-white",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

const notificationDotVariants = tv({
  base: "ml-2 h-2 w-2 flex-shrink-0 rounded-full bg-[#FF4A4A]",
});

interface ListItemProps {
  icon: ReactNode;
  label: string;
  sublabel?: string;
  isActive?: boolean;
  onClick?: () => void;
  to?: string;
  className?: string;
  selected?: boolean;
  hasNewEntry?: boolean;
}

interface ListHeaderProps {
  title: string;
  actionButton?: ReactNode;
}

interface ListContainerProps {
  headerProps: ListHeaderProps;
  className?: string;
  containerHeight?: string | number;
  children: ReactNode;
}

/**
 * リストアイテムコンポーネント - ボタンまたはLinkとして機能
 */
export const ListItem: React.FC<ListItemProps> = ({
  icon,
  label,
  sublabel,
  isActive = false,
  onClick,
  to,
  className = "",
  selected = false,
  hasNewEntry = false,
}) => {
  // 状態の決定
  const state = selected ? "selected" : isActive ? "active" : "default";

  const content = (
    <>
      <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center">{icon}</span>
      <div className="flex flex-grow flex-col">
        <div className="flex items-center justify-between">
          <span
            className={labelVariants()}
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {label}
          </span>
          {hasNewEntry && <span className={notificationDotVariants()} />}
        </div>
        {sublabel && <span className={sublabelVariants({ state })}>{sublabel}</span>}
      </div>
    </>
  );

  const combinedClassName = listItemVariants({ state, className });

  // toプロパティが指定されている場合はリンクとして、そうでない場合はボタンとしてレンダリング
  if (to) {
    return (
      <Link to={to} className={combinedClassName}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={combinedClassName} title={label}>
      {content}
    </button>
  );
};

/**
 * リストコンテナコンポーネント
 */
export const ListContainer: React.FC<ListContainerProps> = ({
  headerProps,
  children,
  className = "",
  containerHeight = "450px",
}) => {
  // 高さの値を適用するためのスタイルオブジェクト
  const containerStyle = {
    height: typeof containerHeight === "number" ? `${containerHeight}px` : containerHeight,
  };

  return (
    <div
      className={`flex w-full flex-col rounded-[0px_17px_0px_0px] bg-custom-white p-1.5 ${className}`}
      style={containerStyle}
    >
      {/* ヘッダー部分 */}
      <div className="flex h-9 items-center justify-between px-1.5">
        <h2 className="font-semibold text-text-subtitle text-xl">{headerProps.title}</h2>
        {headerProps.actionButton}
      </div>

      <hr className="mb-2 rounded-full border-variable-collection-light-gray border-t-2" />

      {/* スクロール可能なコンテナ */}
      <div className="flex min-h-0 flex-grow flex-col overflow-y-auto">{children}</div>
    </div>
  );
};
